import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { QueryConfig } from '@/lib/react-query';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';
import { ParamsWithPagination } from '@/types/api';

import { InfiniteSegments, SegmentFilters } from '../types';

import { getSegments } from './get-segments';
import { segmentsQueryKeys } from './query-keys';

/** How often the segment list catches up with new segments and enrichments. */
export const SEGMENTS_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

type QueryFnType = typeof getSegments;

interface UseSegmentsParams {
  config?: QueryConfig<QueryFnType>;
  params?: ParamsWithPagination<SegmentFilters>;
  filters?: Partial<ProgramBroadcastFiltersState>;
  refreshInterval?: number;
  limit?: number;
}

export function useInfiniteSegments({
  params,
  filters,
  refreshInterval = SEGMENTS_REFRESH_INTERVAL_MS,
  limit = 25,
}: UseSegmentsParams = {}) {
  const queryClient = useQueryClient();
  const lastRefreshTime = useRef(Date.now());

  const query = useInfiniteQuery({
    queryKey: segmentsQueryKeys.infiniteList(filters),
    queryFn: async ({ pageParam = { offset: 0, timestamp: new Date().toISOString() } }) => {
      const response = await getSegments(
        { ...params, offset: pageParam.offset, limit, createdAtLte: pageParam.timestamp },
        filters
      );

      return {
        data: response.data,
        total: response.total,
        timestamp: pageParam.timestamp,
        offset: pageParam.offset,
      };
    },
    initialPageParam: { offset: 0, timestamp: new Date().toISOString() },
    getNextPageParam: lastPage => {
      if (lastPage.data.length < 25) return null;

      return {
        offset: lastPage.offset + lastPage.data.length,
        timestamp: lastPage.timestamp,
      };
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data) return;

    const refreshWithNewAndUpdatedItems = async () => {
      try {
        const existingItems = query.data.pages.flatMap(page => page.data);
        const existingIds = new Set(existingItems.map(item => item.id));

        const currentTime = Date.now();
        const response = await getSegments({ ...params, offset: 0, limit: 25 }, filters);

        const newItems = response.data.filter(item => !existingIds.has(item.id));

        const updatedItemsMap = Object.fromEntries(response.data.map(item => [item.id, item]));

        queryClient.setQueryData(
          segmentsQueryKeys.infiniteList(filters),
          (oldData: InfiniteSegments) => {
            if (!oldData) return oldData;

            const updatedPages = oldData.pages.map(page => {
              const updatedPageData = page.data.map(item => {
                return updatedItemsMap[item.id] || item;
              });

              return {
                ...page,
                data: updatedPageData,
              };
            });

            if (newItems.length > 0) {
              updatedPages[0] = {
                ...updatedPages[0],
                data: [...newItems, ...updatedPages[0].data],
                total: response.total,
              };
            }

            return {
              ...oldData,
              pages: updatedPages,
            };
          }
        );

        lastRefreshTime.current = currentTime;
      } catch (error) {
        console.error('Error refreshing segments:', error);
      }
    };

    const intervalId = setInterval(refreshWithNewAndUpdatedItems, refreshInterval);

    return () => clearInterval(intervalId);
  }, [queryClient, filters, refreshInterval, query.data, params]);

  return query;
}
